import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

import { cn } from '@components/lib/utils';
import { layoutPrefsActions, useLayoutPrefs } from '@hooks/layoutPrefs';

import {
  type NavItem,
  type NavSection,
  isNavItemActive,
  isSectionActive,
  navItemClass,
} from './navConfig';
import SidebarFlyout from './SidebarFlyout';

export interface NavBadge {
  /** Count shown next to the label when expanded (a dot when collapsed). */
  count: number;
  label: string;
}

const CountPill: React.FC<{ count: number }> = ({ count }) => (
  <span className="ml-auto rounded-full bg-marble-red-500 px-1.5 py-px text-[11px] font-semibold leading-4 text-white">
    {count > 99 ? '99+' : count}
  </span>
);

const Dot: React.FC = () => (
  <span className="absolute right-2 top-2 size-2 rounded-full border-[1.5px] border-card bg-marble-red-500" />
);

const selectOpenSections = (s: { sidebarOpenSections: string[] }) =>
  s.sidebarOpenSections;

interface EntryProps {
  section: NavSection;
  badge?: NavBadge;
}

const badgeLabel = (
  section: NavSection,
  badge?: NavBadge,
): string | undefined =>
  badge && badge.count > 0
    ? `${section.label}, ${badge.count} ${badge.label}`
    : undefined;

/** Collapsed rail: an icon link; sections with pages get a flyout. */
const CollapsedEntry: React.FC<EntryProps> = ({ section, badge }) => {
  const location = useLocation();
  const active = isSectionActive(section, location);
  const Icon = section.icon;
  const withBadge = badgeLabel(section, badge);

  if (section.children?.length) {
    return (
      <li className="flex justify-center">
        <SidebarFlyout section={section} badge={withBadge ? <Dot /> : null} />
      </li>
    );
  }
  return (
    <li className="flex justify-center">
      <Link
        to={section.to}
        aria-label={withBadge ?? section.label}
        aria-current={active ? 'page' : undefined}
        className={cn(navItemClass(active, true), 'group')}
      >
        {Icon && <Icon className="size-5 shrink-0" aria-hidden />}
        {withBadge && <Dot />}
        <span
          aria-hidden
          className="pointer-events-none absolute left-[52px] top-1/2 z-50 -translate-y-1/2 whitespace-nowrap rounded-lg border border-marble-bg-800 bg-marble-bg-700 p-2 text-xs text-marble-bg-50 opacity-0 shadow-[4px_4px_8px_rgba(0,0,0,0.07)] transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100"
        >
          {section.label}
        </span>
      </Link>
    </li>
  );
};

const SubList: React.FC<{ id: string; open: boolean; items: NavItem[] }> = ({
  id,
  open,
  items,
}) => {
  const location = useLocation();
  return (
    <ul id={id} hidden={!open} className="mt-0.5 mb-1 flex flex-col gap-0.5">
      {items.map((child) => {
        const childActive = isNavItemActive(child, location);
        return (
          <li key={child.id}>
            <Link
              to={child.to}
              aria-current={childActive ? 'page' : undefined}
              className={cn(
                'flex h-9 min-w-0 items-center px-3 text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-marble-blue-500',
                childActive
                  ? 'bg-card font-semibold text-marble-blue-700 dark:bg-marble-bg-100'
                  : 'text-marble-bg-600 hover:bg-card/60 hover:text-foreground dark:hover:bg-marble-bg-100',
              )}
            >
              <span className="truncate pl-12 pr-2.5">{child.label}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
};

/** Opening a new page reveals its section; collapsing it is local to that page. */
const useSectionDisclosure = (section: NavSection) => {
  const location = useLocation();
  const openSections = useLayoutPrefs(selectOpenSections);

  const active = isSectionActive(section, location);
  const children = section.children ?? [];
  const childPage = children.find((c) => isNavItemActive(c, location));
  const activePageId = active ? childPage?.id ?? section.id : null;
  const [collapsedPage, setCollapsedPage] = useState<string | null>(null);
  useEffect(() => setCollapsedPage(null), [activePageId]);
  const open = active
    ? collapsedPage !== activePageId
    : openSections.includes(section.id);
  const toggle = () => {
    if (active) setCollapsedPage(open ? activePageId : null);
    else layoutPrefsActions.setSectionOpen(section.id, !open);
  };
  return { active, children, childPage, open, toggle };
};

/** Expanded rail: the label is a real link and the chevron is a separate
 * disclosure button. Both share one hover surface. */
const ExpandedEntry: React.FC<EntryProps> = ({ section, badge }) => {
  const { active, children, childPage, open, toggle } =
    useSectionDisclosure(section);
  const listId = `sidebar-section-${section.id}`;
  const Icon = section.icon;
  const withBadge = badgeLabel(section, badge);
  const hasChildren = children.length > 0;

  return (
    <li>
      <div
        className={cn(
          'group/nav-row flex items-center px-3 transition-colors',
          active && !hasChildren
            ? 'bg-card dark:bg-marble-bg-100'
            : 'hover:bg-card/60 dark:hover:bg-marble-bg-100',
        )}
      >
        <Link
          to={section.to}
          aria-label={withBadge}
          aria-current={active && !childPage ? 'page' : undefined}
          className={cn(
            navItemClass(false, false),
            'hover:bg-transparent dark:hover:bg-transparent',
            active
              ? 'text-marble-blue-700 hover:text-marble-blue-700'
              : 'group-hover/nav-row:text-foreground hover:text-foreground',
          )}
        >
          {Icon && <Icon className="size-5 shrink-0" aria-hidden />}
          <span className="truncate">{section.label}</span>
          {withBadge && badge && <CountPill count={badge.count} />}
        </Link>
        {hasChildren && (
          <button
            type="button"
            aria-expanded={open}
            aria-controls={listId}
            aria-label={`${open ? 'Hide' : 'Show'} ${section.label} pages`}
            onClick={toggle}
            className={cn(
              'flex size-9 shrink-0 items-center justify-center rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-marble-blue-500',
              active
                ? 'text-marble-blue-700'
                : 'text-marble-bg-500 group-hover/nav-row:text-foreground',
            )}
          >
            <ChevronDown
              aria-hidden
              className={cn(
                'size-4 transition-transform',
                !open && '-rotate-90',
              )}
            />
          </button>
        )}
      </div>
      {hasChildren && <SubList id={listId} open={open} items={children} />}
    </li>
  );
};

/** One nav entry, in whichever rail is showing. */
const SidebarSection: React.FC<EntryProps & { collapsed: boolean }> = ({
  collapsed,
  ...props
}) =>
  collapsed ? <CollapsedEntry {...props} /> : <ExpandedEntry {...props} />;

export default SidebarSection;
